import styled from "@emotion/styled";
import { groupedRegionColorMap } from "@atlas-int/tokens/classifications";
import { resolveFlagSrc, resolveRegionSrc } from "@atlas-int/ui/Flag";
import { ChevronDown, ChevronUp, DropdownToggleButton, DropdownItem } from "@atlas-int/ui";
import { memo } from "react";
import type { TreeNode } from "@atlas-int/ui";
import { LocationItem, LocationItemLevel } from "./LocationDropdownUtils";
import { surface, spacing, zIndex, opacity, border } from "@atlas-int/tokens";

/**
 * Well-known location IDs. Values match the canonical constants from @atlas-int/data.
 * Defined locally to avoid coupling controls → data.
 */
const LOCATION_IDS = {
  otherGroup: "group-998",
  servicesPartnersGroup: "group-994",
  undeclaredCountry: "country-999",
  unidentifiedServicesCountry: "country-914",
} as const;

const WorldStickyItem = styled(DropdownItem)<{
  $disableRegions?: boolean;
}>`
  position: sticky;
  top: 0;
  left: 0;
  padding-left: 0;
  z-index: ${zIndex.dropdown};
  border-bottom: 1px solid ${border.divider};
  cursor: ${({ $disableRegions }) => ($disableRegions ? "default" : "pointer")};

  background-color: ${({ $selected }) =>
    $selected ? surface.hover : surface.white};

  &:hover {
    background-color: ${surface.hover};
  }
`;

const IconContainer = styled.div`
  flex-basis: 36px;
  width: 36px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LocationIcon = styled.div`
  width: 36px;
  background-size: contain;
  background-position: right center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  & img {
    object-fit: contain;
    max-width: 100%;
  }

  &.icon {
    height: 36px;
    padding: ${spacing[1]};
    box-sizing: border-box;
    & img {
      height: 100%;
      width: 100%;
      box-sizing: border-box;
    }
  }

  &.flag,
  &.world {
    height: 100%;
  }

  &.flag img {
    border: 1px solid ${surface.muted};
  }
`;

const LabelContainer = styled.div`
  width: 100%;
  padding-left: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


const WorldIconImg = styled.img`
  opacity: ${opacity.muted};
  width: 36px;
  margin-left: ${spacing[2]};
  border: 1px solid ${surface.white};
`;

function ChevronIcon({ open }: { open: boolean }) {
  return open ? <ChevronUp /> : <ChevronDown />;
}

interface LocationDropdownItemMUIProps {
  node: TreeNode;
  item: LocationItem;
  style: React.CSSProperties;
  isExpanded: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  hasChildren: boolean;
  level: number;
  onToggle: () => void;
  onClick: () => void;
  disableRegions?: boolean;
  /** Optional icon source for the world item */
  worldIconSrc?: string;
}

const LocationDropdownItemMUI = ({
  item,
  isExpanded,
  isSelected,
  isHighlighted,
  hasChildren,
  onToggle,
  onClick,
  disableRegions = false,
  worldIconSrc,
}: LocationDropdownItemMUIProps) => {
  const {
    id,
    locationLevel,
    hybrid_level_1: regionGroup,
    name_short_en: label,
  } = item;

  const color: string | undefined = groupedRegionColorMap.has(regionGroup!)
    ? groupedRegionColorMap.get(regionGroup!)
    : undefined;

  let imageSource = "";
  if (locationLevel === LocationItemLevel.country) {
    if (item.id === LOCATION_IDS.unidentifiedServicesCountry) {
      imageSource = ""; // No image for Services Partners
    } else if (item.id === LOCATION_IDS.undeclaredCountry) {
      imageSource = resolveFlagSrc("Undeclared");
    } else if (item.iso3Code) {
      imageSource = resolveFlagSrc(item.iso3Code);
    }
  } else if (locationLevel === LocationItemLevel.region) {
    if (id !== LOCATION_IDS.otherGroup && id !== LOCATION_IDS.servicesPartnersGroup) {
      imageSource = resolveRegionSrc(label);
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    if (
      disableRegions &&
      (locationLevel === LocationItemLevel.region ||
        locationLevel === LocationItemLevel.subregion ||
        locationLevel === LocationItemLevel.world)
    ) {
      e.stopPropagation();
      if (hasChildren) {
        onToggle();
      }
      return;
    }
    onClick();
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  // World level - sticky at top
  if (locationLevel === LocationItemLevel.world) {
    return (
      <WorldStickyItem
        $highlighted={isHighlighted}
        $selected={isSelected}
        $disableRegions={disableRegions}
        onClick={handleClick}
      >
        <IconContainer>
          <LocationIcon className="world">
            {worldIconSrc && <WorldIconImg src={worldIconSrc} />}
          </LocationIcon>
        </IconContainer>
        <LabelContainer>{label}</LabelContainer>
      </WorldStickyItem>
    );
  }

  // Services Partners as top-level (not sticky - appears at end of list)
  if (id === LOCATION_IDS.unidentifiedServicesCountry) {
    const servicesPartnersColor = groupedRegionColorMap.get(
      LOCATION_IDS.servicesPartnersGroup,
    );
    return (
      <DropdownItem
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={handleClick}
        style={{ paddingLeft: 0 }}
      >
        <IconContainer style={{ backgroundColor: servicesPartnersColor, height: "auto", alignSelf: "stretch", marginTop: 1, marginBottom: 1 }}>
          <LocationIcon className="icon" />
        </IconContainer>
        <LabelContainer>{label}</LabelContainer>
      </DropdownItem>
    );
  }

  // Region level
  if (locationLevel === LocationItemLevel.region) {
    return (
      <DropdownItem
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={handleClick}
        style={{ paddingLeft: 0, cursor: disableRegions ? "default" : "pointer" }}
      >
        <IconContainer style={{ backgroundColor: color, height: "auto", alignSelf: "stretch", marginTop: 1, marginBottom: 1 }}>
          <LocationIcon className="icon">
            {imageSource && <img src={imageSource} alt="" />}
          </LocationIcon>
        </IconContainer>
        <LabelContainer>{label}</LabelContainer>
        {hasChildren && (
          <DropdownToggleButton type="button" onClick={handleToggleClick}>
            <ChevronIcon open={isExpanded} />
          </DropdownToggleButton>
        )}
      </DropdownItem>
    );
  }

  // Subregion level
  if (locationLevel === LocationItemLevel.subregion) {
    return (
      <DropdownItem
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={handleClick}
        style={{ paddingLeft: 0, cursor: disableRegions ? "default" : "pointer" }}
      >
        <IconContainer
          style={{
            borderRight: `${spacing[2]} solid ${color}`,
            marginLeft: `-${spacing[2]}`,
            height: "auto",
            alignSelf: "stretch",
            marginTop: 1,
            marginBottom: 1,
          }}
        >
          <LocationIcon className="icon" />
        </IconContainer>
        <LabelContainer>{label}</LabelContainer>
        {hasChildren && (
          <DropdownToggleButton type="button" onClick={handleToggleClick}>
            <ChevronIcon open={isExpanded} />
          </DropdownToggleButton>
        )}
      </DropdownItem>
    );
  }

  // Country level
  if (locationLevel === LocationItemLevel.country) {
    return (
      <DropdownItem
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={handleClick}
        style={{ paddingLeft: spacing[4] }}
      >
        <IconContainer
          style={{
            borderLeft: `${spacing[2]} solid ${color}`,
            marginLeft: spacing[20],
            paddingLeft: spacing[2],
            height: "auto",
            alignSelf: "stretch",
            marginTop: 1,
            marginBottom: 1,
          }}
        >
          <LocationIcon className="flag">
            {imageSource && <img src={imageSource} alt="" />}
          </LocationIcon>
        </IconContainer>
        <LabelContainer>{label}</LabelContainer>
      </DropdownItem>
    );
  }

  return null;
};

export default memo(LocationDropdownItemMUI);
